import CasaHttpRequest from 'shared-utils/src/httpLib';
import { setItem } from 'shared-utils/src/localStorage';

import BOOKMARKED_SEARCHES_LS from 'shared-constants/src/bookmarksLSRecord';

import {
  getUpdateSavedSearchBody,
  getCallSaveSearchBody,
  getProvince,
  getLsRecords,
  getBookmarkSummary,
} from './index';

import { APPS_WITH_LS_BOOKMARKS, ENPOINTS, apiCallConfig } from './constants';

const httpLib = new CasaHttpRequest();

const getSearchHash = async ({
  proxyDomain,
  bkmPayload,
  title,
  notifications,
  excludeAuctions,
}) => {
  const reqBody = getCallSaveSearchBody({
    bkmPayload,
    title,
    notifications,
    excludeAuctions,
  });

  const serializeApiResponse = await httpLib.post(
    `${proxyDomain}${ENPOINTS.serialize}`,
    reqBody,
    apiCallConfig,
  );

  let hash = null;
  if (serializeApiResponse.status === 200) {
    const { data: { serialize: { hash: serHash } } } = JSON.parse(serializeApiResponse.data.data);
    hash = serHash;
  }
  return hash;
};

export const getBookmarkPayload = async ({
  filters,
  locations,
  activeNearby,
  total,
  user,
  domain,
  pageType,
}) => {
  delete filters.agencyData;
  const reqBody = {
    filters,
    locations,
    activeNearby,
    total,
    user,
    domain,
    searchType: 'bookmark',
    isFromMap: pageType === 'searchMap',
  };
  const proxyDomain = domain.replace('3000', '5000');
  const computeSaveSearchPayload = await httpLib.post(
    `${proxyDomain}/portal-srp/api/v1/sharedUtils/getBkmPayload`,
    reqBody,
    {
      withCredentials: true,
      headers: {
        accept: 'application/json',
      },
    },
  );

  return computeSaveSearchPayload.data;
};

export const updateBoomark = async ({
  searchTitle,
  notifications,
  bkmPayload,
  bookmarkData,
  user,
  proxyDomain,
  saveBookmarkSuccess,
  locations,
  filters,
  searchHasBeenEdited,
  loadNewSearch,
}) => {
  const reqBody = getUpdateSavedSearchBody({
    searchTitle,
    notifications,
    bookmarkData,
    user,
    bkmPayload,
  });

  const updateSavedSearchApiResponse = await httpLib.post(
    `${proxyDomain}${ENPOINTS.update}`,
    reqBody,
    apiCallConfig,
  );

  if (updateSavedSearchApiResponse.status === 200) {
    if (searchHasBeenEdited) {
      loadNewSearch();
    }

    const savedSearchid = updateSavedSearchApiResponse?.data?.response?.data?.searchId;

    // chiamo la serialize per ottenere lo hash del bookmark
    const hash = await getSearchHash({
      proxyDomain,
      bkmPayload,
      title: searchTitle,
      notifications,
      excludeAuction: null,
    });
    
    if (savedSearchid && saveBookmarkSuccess) {
      const province = getProvince(locations[0]);
      const { searchData: { encodedProperties: { searchUrl } } } = bkmPayload;
      
      const { savedsearchesData, provinces } = getLsRecords();
      const newSavedsearchesData = savedsearchesData.map((bm) => {
        if (bm.id.toString() === savedSearchid.toString()) {
          return {
            id: parseInt(savedSearchid, 10),
            hash,
            summary: getBookmarkSummary(searchTitle, filters, notifications, searchUrl, bookmarkData),
            province,
          };
        }
        return bm;
      });

      if (province && !provinces.includes(province)) {
        provinces.push(province);
      }

      setItem(BOOKMARKED_SEARCHES_LS, { savedsearchesData: newSavedsearchesData, provinces });
      
      saveBookmarkSuccess(savedSearchid, { hash, isUpdate: true });
    }
    return true;
  }
  return false;
};

export const saveBoomark = async ({
  excludeAuctions,
  bkmPayload,
  title,
  notifications,
  setForcedAuction,
  app,
  proxyDomain,
  saveBookmarkSuccess,
  locations,
  filters,
  searchHasBeenEdited,
  loadNewSearch,
}) => {  
  const reqBody = getCallSaveSearchBody({
    bkmPayload,
    title,
    notifications,
    excludeAuctions,
  });

  const saveSearchApiResponse = await httpLib.post(
    `${proxyDomain}${ENPOINTS.save}`,
    reqBody,
    apiCallConfig,
  );

  if (saveSearchApiResponse.status === 200) {
    if (searchHasBeenEdited) {
      loadNewSearch(excludeAuctions);
    } else if (excludeAuctions && setForcedAuction) {
      setForcedAuction();
    }

    // se la richiesta è stata inviata da un'app che salva in localstorage il record relativo ai bookmarks
    if (APPS_WITH_LS_BOOKMARKS.includes(app)) {
      // recupero il searchId
      const { data: { data: searchId } } = saveSearchApiResponse;

      // chiamo la serialize per ottenere lo hash del bookmark
      const hash = await getSearchHash({
        proxyDomain,
        bkmPayload,
        title,
        notifications,
        excludeAuctions,
      });

      // aggiorno il record localstorage relativo ai bookmarks
      if (hash && searchId) {
        const province = getProvince(locations[0]);
        const { searchData: { encodedProperties: { searchUrl } } } = bkmPayload;
        const { savedsearchesData, provinces } = getLsRecords();

        savedsearchesData.unshift({
          id: searchId,
          hash,
          summary: getBookmarkSummary(title, filters, notifications, searchUrl),
          province,
        });

        if (province && !provinces.includes(province)) {
          provinces.push(province);
        }

        // Elimina i duplicati
        const uniqueArray = Object.values(
          savedsearchesData.reduce((acc, curr) => {
            acc[curr.hash] = curr;
            return acc;
          }, {})
        );

        setItem(BOOKMARKED_SEARCHES_LS, { savedsearchesData: uniqueArray, provinces });
      }

      saveBookmarkSuccess && saveBookmarkSuccess(searchId, { hash, isUpdate: false });
    }
    return true;
  }
  return false;
};

export const deleteBoomark = async ({
  user,
  searchId,
  proxyDomain,
  app,
  locations,
  deleteBookmarkSuccess,
}) => {
  const reqBody = {
    user,
    data: {
      searchType: 'bookmark',
      id: [searchId],
    },
  };

  const deleteSavedSearchApiResponse = await httpLib.post(
    `${proxyDomain}${ENPOINTS.delete}`,
    reqBody,
    apiCallConfig,
  );

  if (deleteSavedSearchApiResponse.status === 200) {
    if (APPS_WITH_LS_BOOKMARKS.includes(app)) {

      const province = getProvince(locations[0]);
      
      const { savedsearchesData, provinces } = getLsRecords();

      const newSavedsearchesData = savedsearchesData.filter(bm => bm.id.toString() !== searchId.toString());

      if (province) {
        const provinceIndex = provinces.indexOf(province);
        const otherBookmarksProvinceList = newSavedsearchesData.map(bm => bm.summary.province);
        // cancello la provincia solo se non esiste nelle altre ricerche salvate 
        if (provinceIndex > -1 && !otherBookmarksProvinceList.includes(province)) {
          provinces.splice(provinceIndex, 1);
        }
      }

      setItem(BOOKMARKED_SEARCHES_LS, { savedsearchesData: newSavedsearchesData, provinces });
      
      deleteBookmarkSuccess && deleteBookmarkSuccess(); 
    }
    return true;
  }
  return false;
};